import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';

// Benchling AI brand gradient (Nucleus blue → grape), also used in AI chat surfaces.
export const AI_GRADIENT = `linear-gradient(135deg, ${Color.BLUE_500} 0%, ${Color.GRAPE_400} 100%)`;

/** Vertical rhythm between major blocks on a signup step (header, form, CTA). */
export const SIGNUP_STEP_STACK_SPACING = 32;

/** Tight gap between the back link and step title. */
export const SIGNUP_BACK_HEADER_STACK_SPACING = 'xs';

/** Gap between the step title and its supporting line. */
export const SIGNUP_HEADER_STACK_SPACING = 'xs';

/** Gap between form fields within a step. */
export const SIGNUP_FORM_STACK_SPACING = 24;

/** Gap within a tightly related block, such as a heading and its supporting content. */
export const SIGNUP_COMPACT_STACK_SPACING = 12;

/** Comfortable tap target without oversized fields. */
const SIGNUP_CONTROL_HEIGHT_PX = 40;

export const SIGNUP_CONTROL_SIZE = 'md';
export const SIGNUP_BUTTON_SIZE = 'md';
export const SIGNUP_CHECKBOX_SIZE = 'md';

export const SIGNUP_CARD_MAX_WIDTH_PX = 480;

interface SignupControlClassNames {
  controlDescription: string;
  controlInput: string;
  controlLabel: string;
}

export function signupControlClassNames(classes: SignupControlClassNames): {
  description: string;
  input: string;
  label: string;
} {
  return {
    description: classes.controlDescription,
    input: classes.controlInput,
    label: classes.controlLabel,
  };
}

export const useSignupStepStyles = createStyles((theme) => ({
  subtitle: {
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.md,
    lineHeight: 1.5,
  },
  controlLabel: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  controlDescription: {
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.5,
  },
  checkboxLabel: {
    fontSize: theme.fontSizes.md,
    lineHeight: 1.5,
  },
  workFocusChipLabel: {
    '&&': {
      backgroundColor: theme.white,
      borderColor: theme.colors.gray[4],
      fontWeight: 400,
      height: 'auto',
      lineHeight: 1.25,
      marginBottom: 0,
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      transition: 'background-color 100ms ease, border-color 100ms ease',
      '&:hover': {
        backgroundColor: theme.colors.gray[0],
        borderColor: theme.colors.gray[4],
      },
      '&[data-checked]': {
        backgroundColor: theme.colors.blue[0],
        borderColor: theme.colors.blue[4],
        color: theme.colors.blue[8],
        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
        '&:hover': {
          backgroundColor: theme.colors.blue[1],
          borderColor: theme.colors.blue[4],
        },
      },
    },
  },
  workFocusChipIconWrapper: {
    display: 'none',
  },
  controlInput: {
    height: SIGNUP_CONTROL_HEIGHT_PX,
    minHeight: SIGNUP_CONTROL_HEIGHT_PX,
  },
}));
